import React from 'react';
import PropTypes from 'prop-types';
import { get, isEmpty } from 'lodash-es';
import { bindToPath, connectToModel } from 'client/data/luckdragon/redux/react-binding';
import { EvInsightsEntities, EvInsightsModel, EvInsightsPaths, INVENTORY_CODE } from 'client/data/models/ev-insights';
import { getWizardIncentivesPath, IncentiveModel, IncentivesEntities } from 'client/data/models/incentives';
import { buildReviewPath, EditorialReviewModel } from 'client/data/models/editorial-review';
import { getParamsFromVehicle } from 'site-modules/shared/utils/core-page/params';
import {
  buildEvTestedDataPath,
  buildFuelCostsPath,
  VehicleEntities,
  VehicleModel,
} from 'client/data/models/vehicle-v2';
import { VisitorEntities, VisitorModel } from 'client/data/models/visitor';
import { EV_TESTED_DATA_START_DATE } from 'site-modules/shared/constants/range-and-cost';
import { getUsedYears } from 'site-modules/shared/utils/core-page/ev-insights';
import { ScrollElement } from 'site-modules/shared/components/scroll-link/scroll-element';
import { ReadyForEv, ReadyForEvTreehouse } from 'site-modules/shared/components/ready-for-ev/ready-for-ev';
import { RangeInsight } from 'site-modules/shared/components/range-insight/range-insight';
import { TreehousePromotionCheck } from 'site-modules/shared/components/treehouse-promotion/treehouse-promotion-check';
import { InsightAd } from 'site-modules/shared/components/native-ad/insight-ad/insight-ad';
import { ChargingInsight } from 'site-modules/shared/components/charging-insight/charging-insight';
import { LOAN_PAYMENT } from 'site-modules/shared/constants/allowed-inventory-request-params';
import { IncentivesWizardWidget } from 'site-modules/shared/components/incentives/incentives-wizard/incentives-wizard-widget';
import { INVENTORY_TYPES } from 'client/constants/inventory-types';
import { CREATIVE_ID } from 'site-modules/core-page/constants/ev-insights';
import { EdmundsTestedBadge } from 'site-modules/core-page/components/edmunds-tested-badge/edmunds-tested-badge';
import { getDefaultPlaceholderNameAds } from 'site-modules/shared/components/native-ad/utils/utils';
import { BatteryLifeInsight } from './insights/battery-life-insight/battery-life-insight';
import { CostToDriveInsight } from './insights/cost-to-drive-insight/cost-to-drive-insight';
import { IncentivesWizardInsight } from './insights/incentives-wizard-insight/incentives-wizard-insight';
import { InsightCard } from './insight-card';

function InsightsCardsUI({
  vehicle: {
    make: { name: makeName, slug: makeSlug },
    model: { name: modelName, slug: modelSlug },
    year,
  },
  styleId = null,
  setModelValue,
  evInsightsData = null,
  wizardIncentives = null,
  styleEvTestedData = null,
  fuelCostsData = null,
  reviewEstimatedRange = null,
  visitorLocation = null,
  isPluginHybrid = false,
  isUsed = false,
  isMobile = false,
  isInternational = false,
  isClientSiteServedAds = false,
}) {
  if (!fuelCostsData) {
    return null;
  }

  const testedRange = get(styleEvTestedData, 'range');
  const epaRange = get(evInsightsData, 'style.epaSpecifications.electricityRange');
  const usedYears = getUsedYears({ year, isUsed });

  const batteryWarranty = get(evInsightsData, 'style.batteryWarranty');
  const hybridComponentsWarranty = get(evInsightsData, 'style.hybridComponentsWarranty');

  const batteryLifeInsight = (
    <InsightCard
      heading={batteryWarranty || !hybridComponentsWarranty ? 'EV Battery Warranty' : 'Hybrid Parts Warranty'}
      creativeId={CREATIVE_ID.BATTERY_LIFE}
    >
      <BatteryLifeInsight
        batteryWarranty={batteryWarranty}
        hybridComponentsWarranty={hybridComponentsWarranty}
        usedYears={usedYears}
      />
    </InsightCard>
  );

  const evRangeHeading = isUsed ? 'Estimated Range Based on Age' : 'Range';

  const readyForEv = !isPluginHybrid && (
    <InsightCard heading="Am I Ready for an EV?">
      <ReadyForEv isMobileLayout />
    </InsightCard>
  );

  return (
    <div className="insights-cards mb-1_5" style={{ columns: isMobile ? 1 : 2 }}>
      <InsightCard
        heading={isPluginHybrid ? 'All Electric Range' : evRangeHeading}
        creativeId={CREATIVE_ID.RANGE}
        badge={testedRange ? <EdmundsTestedBadge /> : undefined}
      >
        <RangeInsight
          vehicleName={`${year} ${makeName} ${modelName}`}
          modelName={modelName}
          year={year}
          testedRange={testedRange}
          epaRange={epaRange}
          reviewEstimatedRange={reviewEstimatedRange}
          visitorLocation={visitorLocation}
          isUsed={isUsed}
          isPluginHybrid={isPluginHybrid}
          isInternational={isInternational}
        />
      </InsightCard>
      {isMobile && batteryLifeInsight}
      <InsightCard tag={ScrollElement} id="subnav-ev-charging" heading="Charging" creativeId={CREATIVE_ID.CHARGING}>
        <ChargingInsight
          chargeTime={get(evInsightsData, 'style.epaSpecifications.batteryChargeTime')}
          portType={get(evInsightsData, 'style.engine.electricMotor.fastChargePortType')}
          testedChargeTime={get(styleEvTestedData, 'totalChargingTimeMMSS')}
          creativeId={CREATIVE_ID.CHARGING}
        />
      </InsightCard>
      {!isMobile && batteryLifeInsight}
      <InsightAd
        mobile={isMobile}
        wrapperClass="insight-card mb-1 w-100"
        placeholderName={isClientSiteServedAds ? getDefaultPlaceholderNameAds(isMobile) : null}
      />
      <InsightCard heading="EV Tax Credits & Rebates" creativeId={CREATIVE_ID.INCENTIVES}>
        <IncentivesWizardWidget
          availableIncentives={wizardIncentives || []}
          zipCode={visitorLocation?.zipCode}
          inventoryType={isUsed ? INVENTORY_TYPES.USED : INVENTORY_TYPES.NEW}
          paymentType={LOAN_PAYMENT}
          engineTypes={[evInsightsData?.style?.engine?.engineType?.toLowerCase()]}
        >
          {({ wizardState, incentives: eligibleIncentives, onFinish, onStartOver }) => (
            <IncentivesWizardInsight
              incentives={eligibleIncentives}
              setModelValue={setModelValue}
              styleId={styleId}
              makeName={makeName}
              modelName={modelName}
              makeSlug={makeSlug}
              modelSlug={modelSlug}
              year={year}
              engineType={evInsightsData?.style?.engine?.engineType}
              zipCode={visitorLocation?.zipCode}
              isUsed={isUsed}
              isMobile={isMobile}
              onFinish={onFinish}
              onStartOver={onStartOver}
              wizardState={wizardState}
              isInternational={isInternational}
            />
          )}
        </IncentivesWizardWidget>
      </InsightCard>
      {!isEmpty(fuelCostsData) && (
        <InsightCard heading="Cost to Drive" creativeId={CREATIVE_ID.COST_TO_DRIVE}>
          <CostToDriveInsight
            modelName={modelName}
            fuelCostsData={fuelCostsData}
            stateName={get(visitorLocation, 'state')}
          />
        </InsightCard>
      )}
      <TreehousePromotionCheck fallback={readyForEv}>
        <InsightCard
          heading={isPluginHybrid ? 'Am I Ready for a Plug-In Hybrid?' : 'Am I Ready for an EV?'}
          creativeId={CREATIVE_ID.TREEHOUSE_PROMOTION}
        >
          <ReadyForEvTreehouse isMobileLayout creativeId={CREATIVE_ID.TREEHOUSE_PROMOTION} />
        </InsightCard>
      </TreehousePromotionCheck>
    </div>
  );
}

InsightsCardsUI.propTypes = {
  vehicle: VehicleEntities.MakeModelSubmodelYear.isRequired,
  setModelValue: PropTypes.func.isRequired,
  styleId: PropTypes.number,
  evInsightsData: EvInsightsEntities.EvInsightsData,
  wizardIncentives: PropTypes.arrayOf(IncentivesEntities.IncentiveGraphql),
  styleEvTestedData: VehicleEntities.StyleEvTestedData,
  fuelCostsData: VehicleEntities.StyleFuelCostData,
  reviewEstimatedRange: PropTypes.number,
  visitorLocation: VisitorEntities.Location,
  isPluginHybrid: PropTypes.bool,
  isUsed: PropTypes.bool,
  isMobile: PropTypes.bool,
  isInternational: PropTypes.bool,
  isClientSiteServedAds: PropTypes.bool,
};

export const propsAreEqual = (
  prevProps,
  { evInsightsData, fuelCostsData, styleEvTestedData, vehicle, isPluginHybrid }
) =>
  (!isPluginHybrid && !styleEvTestedData && vehicle.year >= EV_TESTED_DATA_START_DATE) ||
  !evInsightsData ||
  !fuelCostsData;
export const InsightsCards = connectToModel(React.memo(InsightsCardsUI, propsAreEqual), {
  visitorLocation: bindToPath('location', VisitorModel),
  styleEvTestedData: bindToPath(
    ({ styleId, vehicle, isPluginHybrid }) =>
      !isPluginHybrid && vehicle.year >= EV_TESTED_DATA_START_DATE && buildEvTestedDataPath([styleId]),
    VehicleModel,
    evTestedData => get(evTestedData, '[0]', {})
  ),
  evInsightsData: bindToPath(
    ({ styleId, isUsed }) =>
      EvInsightsPaths.getEvInsightsDataPath(styleId, isUsed ? INVENTORY_CODE.USED : INVENTORY_CODE.NEW),
    EvInsightsModel,
    null,
    false
  ),
  wizardIncentives: bindToPath(({ styleId, isUsed }) => getWizardIncentivesPath(styleId, isUsed), IncentiveModel),
  fuelCostsData: bindToPath(({ styleId }) => buildFuelCostsPath(styleId), VehicleModel),
  reviewEstimatedRange: bindToPath(
    ({ vehicle }) => buildReviewPath(getParamsFromVehicle(vehicle)),
    EditorialReviewModel,
    ({ estimatedRange }) => estimatedRange
  ),
});
